import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuoteModal } from '../../hooks/useQuoteModal';
export function HeroSection() {
  const navigate = useNavigate();
  const {
    openModal
  } = useQuoteModal();
  return <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-[#F8F8F6]" data-id="element-270">
      <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-271">
        {/* Text Content */}
        <div className="z-10 order-2 lg:order-1" data-id="element-272">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="text-4xl md:text-6xl lg:text-7xl font-bold font-heading text-[#0C0C0C] leading-[1.1] mb-6 tracking-tight" data-id="element-273">
            <span className="text-[#00316B]" data-id="element-274">
              Building digital experiences that drive real{' '}
            </span>
            <span className="text-[#FF6600]" data-id="element-275">business growth</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: 'easeOut'
        }} className="text-lg md:text-xl text-gray-600 mb-8 max-w-lg leading-relaxed" data-id="element-276">
            We help businesses and brands design, build, and scale modern
            digital solutions - from strategy and design to development and
            growth.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4,
          ease: 'easeOut'
        }} className="flex flex-col sm:flex-row gap-4" data-id="element-277">
            <Button size="lg" className="bg-[#FF6600] text-white hover:bg-[#CC5200] px-8" onClick={openModal} data-id="element-278">
              Request a Quote
            </Button>
            <Button variant="outline" size="lg" className="border-gray-300 hover:bg-white" onClick={() => navigate('/work')} data-id="element-279">
              View Our Work
            </Button>
          </motion.div>
        </div>

        {/* Visual Content */}
        <div className="relative h-[400px] md:h-[600px] w-full flex items-center justify-center order-1 lg:order-2" data-id="element-280">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1,
          delay: 0.5
        }} className="relative w-full h-full" data-id="element-281">
            {/* Abstract Composition */}
            <svg viewBox="0 0 600 600" className="w-full h-full absolute inset-0 text-[#2D2D6B]/10" data-id="element-282">
              <motion.circle cx="300" cy="300" r="150" fill="currentColor" initial={{
              scale: 0
            }} animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              scale: {
                duration: 8,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: 'easeInOut'
              }
            }} data-id="element-283" />
              <motion.rect x="100" y="100" width="200" height="200" fill="#E5E7EB" className="opacity-50" initial={{
              x: -50,
              opacity: 0
            }} animate={{
              x: [100, 110, 100],
              y: [100, 90, 100],
              opacity: 0.5
            }} transition={{
              x: {
                duration: 10,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: 'easeInOut'
              },
              y: {
                duration: 12,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: 'easeInOut'
              },
              opacity: {
                duration: 1,
                delay: 0.8
              }
            }} data-id="element-284" />
              <motion.rect x="350" y="250" width="150" height="250" fill="#00316B" className="opacity-20" initial={{
              y: 50,
              opacity: 0
            }} animate={{
              y: [250, 240, 250],
              opacity: 0.2
            }} transition={{
              y: {
                duration: 9,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: 'easeInOut'
              },
              opacity: {
                duration: 1,
                delay: 1
              }
            }} data-id="element-285" />
              <motion.circle cx="450" cy="150" r="30" fill="#0C0C0C" className="opacity-10" initial={{
              scale: 0
            }} animate={{
              scale: [1, 1.15, 1]
            }} transition={{
              scale: {
                duration: 6,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: 'easeInOut'
              }
            }} data-id="element-286" />
              {/* Decorative Lines */}
              <motion.line x1="50" y1="500" x2="550" y2="500" stroke="#0C0C0C" strokeWidth="1" className="opacity-10" initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 1.5,
              delay: 0.5
            }} data-id="element-287" />
              <motion.line x1="500" y1="50" x2="500" y2="550" stroke="#0C0C0C" strokeWidth="1" className="opacity-10" initial={{
              pathLength: 0
            }} animate={{
              pathLength: 1
            }} transition={{
              duration: 1.5,
              delay: 0.7
            }} data-id="element-288" />
            </svg>

            {/* Floating UI Card Mockup */}
            <motion.div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-xl border border-white/50 w-64 md:w-80" initial={{
            y: 50,
            opacity: 0
          }} animate={{
            y: ['-50%', 'calc(-50% - 8px)', '-50%'],
            opacity: 1
          }} transition={{
            y: {
              duration: 5,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut'
            },
            opacity: {
              duration: 0.8,
              delay: 1.4
            }
          }} data-id="element-289">
              <div className="flex items-center gap-3 mb-4" data-id="element-290">
                <motion.div className="w-10 h-10 rounded-full bg-gray-100" animate={{
                scale: [1, 1.05, 1]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: 'easeInOut'
              }} data-id="element-291" />
                <div className="space-y-2" data-id="element-292">
                  <div className="h-2 w-24 bg-gray-200 rounded" data-id="element-293"></div>
                  <div className="h-2 w-16 bg-gray-100 rounded" data-id="element-294"></div>
                </div>
              </div>
              <motion.div className="h-24 bg-gray-50 rounded-lg mb-4" animate={{
              opacity: [1, 0.7, 1]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut'
            }} data-id="element-295" />
              <div className="flex justify-between items-center" data-id="element-296">
                <motion.div className="h-8 w-20 bg-[#FF6600] rounded-md" animate={{
                opacity: [1, 0.8, 1]
              }} transition={{
                duration: 2.5,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: 'easeInOut'
              }} data-id="element-297" />
                <div className="h-8 w-8 rounded-full border border-gray-200" data-id="element-298"></div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}